import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { DateRangeFilter } from 'components/ui/date-range-filter';
import { FilterButton, type FilterOption } from 'components/ui/filter-button';
import { useFeature, FEATURES } from 'modules/entitlements';
import { useTestFilterAuthors, useTestFilterCollections, type TestFilterValue } from './testFilterOptions';

export { hasTestFilters, type TestFilterValue } from './testFilterOptions';

interface TestFiltersProps {
  orgId?: string;
  value: TestFilterValue;
  onChange: (next: TestFilterValue) => void;
  /** The signed-in user's id — listed first as "Created by me". */
  currentUserId?: string;
}

/**
 * The Tests list's filters, inline on the filter line: author, collection and
 * created date. Author and collection options load the first time their
 * popover opens.
 */
export function TestFilters({ orgId, value, onChange, currentUserId }: TestFiltersProps) {
  const { t } = useTranslation();
  const canUseCollections = useFeature(FEATURES.COLLECTIONS);
  const [authorsOpened, setAuthorsOpened] = React.useState(false);
  const [collectionsOpened, setCollectionsOpened] = React.useState(false);

  const membersQuery = useTestFilterAuthors(orgId, authorsOpened || Boolean(value.createdById));
  const collectionsQuery = useTestFilterCollections(
    orgId,
    canUseCollections && (collectionsOpened || Boolean(value.collectionId)),
  );

  const authorOptions: FilterOption[] = [
    ...(currentUserId ? [{ value: currentUserId, label: t('created_by_me') }] : []),
    ...(membersQuery.data ?? [])
      .filter((m) => m.id !== currentUserId)
      .map((m) => ({ value: m.id, label: m.name })),
  ];
  const collectionOptions: FilterOption[] = (collectionsQuery.data ?? []).map((c) => ({ value: c.id, label: c.name }));

  return (
    <>
      <FilterButton
        label={t('filter_author')}
        options={authorOptions}
        value={value.createdById}
        onChange={(createdById) => onChange({ ...value, createdById })}
        onOpenChange={(open) => open && setAuthorsOpened(true)}
        loading={membersQuery.isLoading}
        emptyLabel={membersQuery.isError ? t('failed_to_load') : t('no_authors')}
        valueLabel={value.createdById === currentUserId ? t('created_by_me') : undefined}
        searchPlaceholder={t('search')}
      />
      {canUseCollections && (
        <FilterButton
          label={t('collection')}
          options={collectionOptions}
          value={value.collectionId}
          onChange={(collectionId) => onChange({ ...value, collectionId })}
          onOpenChange={(open) => open && setCollectionsOpened(true)}
          loading={collectionsQuery.isLoading}
          emptyLabel={collectionsQuery.isError ? t('failed_to_load') : t('no_collections')}
          searchPlaceholder={t('search')}
        />
      )}
      <DateRangeFilter
        label={t('created_at')}
        value={{ from: value.createdFrom, to: value.createdTo }}
        onChange={({ from, to }) => onChange({ ...value, createdFrom: from, createdTo: to })}
      />
    </>
  );
}

export default TestFilters;
